import type { Site, SiteCreate, SiteKind } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

const KINDS: SiteKind[] = ['store', 'truck'];

/** The owner's list of places and companies: add a store or a truck, add a company, close a site. */
export function SitesPage() {
  const { t } = useLocale();
  const qc = useQueryClient();
  const sites = useQuery({ queryKey: qk.sites, queryFn: ({ signal }) => api.sites(signal) });
  const companies = useQuery({ queryKey: qk.companies, queryFn: ({ signal }) => api.companies(signal) });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.sites });
    void qc.invalidateQueries({ queryKey: qk.companies });
  };
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<SiteKind>('store');
  const [companyId, setCompanyId] = useState('');
  const [address, setAddress] = useState('');
  const [coCode, setCoCode] = useState('');
  const [coName, setCoName] = useState('');
  const addSite = useMutation({
    mutationFn: (body: SiteCreate) => api.createSite(body),
    onSuccess: () => {
      setCode('');
      setName('');
      setAddress('');
      invalidate();
    },
  });
  const addCompany = useMutation({
    mutationFn: () => api.createCompany({ code: coCode, name: coName }),
    onSuccess: () => {
      setCoCode('');
      setCoName('');
      invalidate();
    },
  });
  const toggle = useMutation({ mutationFn: (s: Site) => api.patchSite(s.id, { active: !s.active }), onSuccess: invalidate });
  const codeOk = /^[A-Za-z0-9]{2,8}$/.test(code.trim());

  return (
    <div className="stack">
      <PageHead title={t('sitesTitle')} back="/" />
      <p className="muted">{t('sitesHint')}</p>
      <QueryView query={sites}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list" data-testid="sites">
              {rows.map((s) => (
                <li key={s.id} className="row">
                  <span className="row-main">
                    <span className="row-title">
                      {s.name} <span className="label">{s.code}</span>
                      {!s.active ? <span className="badge">{t('inactive')}</span> : null}
                    </span>
                    <span className="row-sub">
                      {t(`kind_${s.kind}`)} · {s.companyName}
                      {s.address ? ` · ${s.address}` : ''}
                    </span>
                    {s.customerId ? (
                      <span className="row-sub">
                        <Link to={`/customers/${s.customerId}`}>{t('billsAsCustomer')}</Link>
                      </span>
                    ) : null}
                  </span>
                  {s.kind !== 'hub' ? (
                    <span className="row-end">
                      <button type="button" className="btn btn-ghost" disabled={toggle.isPending} onClick={() => { if (!s.active || window.confirm(`${t('closeSite')} ${s.name}: ${t('confirmSure')}`)) toggle.mutate(s); }}>
                        {s.active ? t('closeSite') : t('reopenSite')}
                      </button>
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>

      <section className="card stack-sm" data-testid="add-site">
        <h2>{t('addSite')}</h2>
        <div className="two-up">
          <label className="field">
            <span>{t('siteCode')}</span>
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={8} placeholder="STORE1" />
          </label>
          <label className="field">
            <span>{t('kind')}</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as SiteKind)}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`kind_${k}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span>{t('name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </label>
        <label className="field">
          <span>{t('company')}</span>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            <option value="">{t('homeCompany')}</option>
            {(companies.data ?? []).slice(1).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('address')}</span>
          <input value={address} onChange={(e) => setAddress(e.target.value)} maxLength={300} />
        </label>
        <p className="muted">{t('storeOfOtherCompanyHint')}</p>
        {addSite.isError ? <ErrorBox error={addSite.error} /> : null}
        <button
          type="button"
          className="btn btn-primary btn-block"
          disabled={!codeOk || !name.trim() || addSite.isPending}
          onClick={() => addSite.mutate({ code: code.trim(), name: name.trim(), kind, ...(companyId ? { companyId } : {}), ...(address.trim() ? { address: address.trim() } : {}) })}
        >
          {t('addSite')}
        </button>
      </section>

      <section className="card stack-sm" data-testid="companies">
        <h2>{t('companies')}</h2>
        <QueryView query={companies}>
          {(rows) => (
            <ul className="list plain">
              {rows.map((c) => (
                <li key={c.id} className="row">
                  <span className="row-main">
                    <span className="row-title">
                      {c.name} <span className="label">{c.code}</span>
                    </span>
                    <span className="row-sub">
                      {c.siteCount} {t('sites')}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </QueryView>
        <div className="two-up">
          <label className="field">
            <span>{t('companyCode')}</span>
            <input value={coCode} onChange={(e) => setCoCode(e.target.value.toUpperCase())} maxLength={8} placeholder="FFL" />
          </label>
          <label className="field">
            <span>{t('name')}</span>
            <input value={coName} onChange={(e) => setCoName(e.target.value)} maxLength={120} />
          </label>
        </div>
        {addCompany.isError ? <ErrorBox error={addCompany.error} /> : null}
        <button type="button" className="btn btn-secondary btn-block" disabled={!/^[A-Za-z0-9]{2,8}$/.test(coCode.trim()) || !coName.trim() || addCompany.isPending} onClick={() => addCompany.mutate()}>
          {t('addCompany')}
        </button>
      </section>
    </div>
  );
}
